import React, { Component } from "react";
import Layout from "../../../Layout/layout";
import Helmet from "react-helmet";
import ReportButtonGroup from "../../commoncomponents/reportButtonGroup";
import TimeZoneConvert from "../../../utils/timeZoneConvert";
import "../../../styles/reports/reports.scss";
import "../../../styles/tools/colors.scss";
import { Button } from "react-bootstrap";
import Calendar from "react-calendar/dist/umd/Calendar";
import { connect } from "react-redux";
import {
  setErrorMessage,
  setHeaderVisible,
  setReportAssetId,
  setReportCalendatDate,
  setReportAssetName,
  setReportGroupId,
} from "../../../reducers/actions";
import moment from "moment-timezone/builds/moment-timezone-with-data";
import axios from "axios";
import { apiHost } from "../../../api/apiClient";
import queryString from "query-string";
import Select from "react-select";
import GridLoader from "react-spinners/GridLoader";
import AssetInforWarning from "../../commoncomponents/assetInforWarning";
import GroupWarning from "../../commoncomponents/groupWarning";

const REPORT_FILTERING_STORAGE_KEY = "reportUseFiltering";
const MIN_SEGMENT_PERCENT = 0.18;
const DEFAULT_ZOOM_START = "00:00";
const DEFAULT_ZOOM_END = "24:00";
const DAY_MINUTES = 24 * 60;
const ZOOM_STEP_MINUTES = 15;
const OVERLAY_METRICS = {
  speed: {
    label: "SPEED",
    unit: "km/h",
    keys: ["speed", "Speed"],
    floorAtZero: true,
    strokeClass: "speed",
  },
  tempA: {
    label: "TEMP A",
    unit: "\u00b0C",
    keys: ["tempA", "TempA", "sensorAValue", "SensorAValue"],
    minValidValue: -80,
    maxValidValue: 80,
    strokeClass: "temperature-a",
  },
  tempB: {
    label: "TEMP B",
    unit: "\u00b0C",
    keys: ["tempB", "TempB", "sensorBValue", "SensorBValue"],
    minValidValue: -80,
    maxValidValue: 80,
    strokeClass: "temperature-b",
  },
};

const getSavedReportFilteringState = () => {
  const savedValue = localStorage.getItem(REPORT_FILTERING_STORAGE_KEY);
  return savedValue === null ? true : savedValue !== "false";
};

const getTimeZone = () => localStorage.getItem("timeZoneID") || moment.tz.guess();

const getValue = (item, keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    if (item && item[keys[index]] !== undefined && item[keys[index]] !== null) {
      return item[keys[index]];
    }
  }

  return null;
};

const isFridgeOn = (value) =>
  value == 1 || value === true || String(value).toLowerCase() === "true" || String(value).toLowerCase() === "on";

const isDoorOpen = (value) =>
  value == 0 || String(value).toLowerCase() === "open" || String(value).toLowerCase() === "false";

const clampPercent = (value) => Math.max(0, Math.min(100, value));

const getNumberValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const getIo8Value = (point) => {
  const value = getValue(point, ["iO8", "IO8", "io8", "Io8"]);
  const numberValue = Number(value);

  return Number.isFinite(numberValue) && (numberValue === 0 || numberValue === 1) ? numberValue : null;
};

const getBooleanParam = (value, fallback = true) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  const text = String(value).trim().toLowerCase();
  return !["0", "false", "no", "off"].includes(text);
};

const formatDuration = (start, end) => {
  const totalMinutes = Math.max(0, Math.round(moment.duration(end.diff(start)).asMinutes()));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
};

const toTimelineMoment = (date) => {
  if (!date) return null;

  const parsed = moment(date);
  return parsed.isValid() ? parsed.tz(getTimeZone()) : null;
};

const getMinutesFromTime = (timeValue) => {
  const [hours, minutes] = String(timeValue || "").split(":").map((value) => Number(value));

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }

  return Math.max(0, Math.min(DAY_MINUTES, hours * 60 + minutes));
};

const formatTimeFromMinutes = (minutes) => {
  const clampedMinutes = Math.max(0, Math.min(DAY_MINUTES, Number(minutes)));
  const hours = Math.floor(clampedMinutes / 60);
  const mins = clampedMinutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

const getTimelineMomentForTime = (selectedDate, timeValue) => {
  const timeZone = getTimeZone();
  const dayStart = moment.tz(selectedDate, "YYYY-MM-DD", timeZone).startOf("day");
  const minutes = getMinutesFromTime(timeValue);

  return dayStart.clone().add(minutes === null ? 0 : minutes, "minutes");
};

const getDayBounds = (selectedDate, zoomStartTime = DEFAULT_ZOOM_START, zoomEndTime = DEFAULT_ZOOM_END) => {
  const timeZone = getTimeZone();
  const dayStart = moment.tz(selectedDate, "YYYY-MM-DD", timeZone).startOf("day");
  const fullDayEnd = dayStart.clone().add(1, "day");
  const now = moment().tz(timeZone);
  const reportDataEnd = now.isSame(dayStart, "day") ? moment.min(now, fullDayEnd) : fullDayEnd;
  let start = getTimelineMomentForTime(selectedDate, zoomStartTime);
  let end = getTimelineMomentForTime(selectedDate, zoomEndTime);

  if (!end.isAfter(start)) {
    start = dayStart.clone();
    end = fullDayEnd.clone();
  }

  return {
    start,
    end,
    dataEnd: moment.min(reportDataEnd, end),
    reportDataEnd,
  };
};

const makePositionedSegments = (segments, dayStart, dayEnd, clipEnd = dayEnd) => {
  const dayMs = dayEnd.diff(dayStart);

  return segments
    .map((segment) => {
      const start = moment.max(segment.start, dayStart);
      const end = moment.min(segment.end, clipEnd);

      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
        return null;
      }

      const left = clampPercent((start.diff(dayStart) / dayMs) * 100);
      const width = Math.max(MIN_SEGMENT_PERCENT, clampPercent((end.diff(start) / dayMs) * 100));

      return {
        ...segment,
        start,
        end,
        left,
        width: Math.min(width, 100 - left),
        durationLabel: formatDuration(start, end),
        timeLabel: `${start.format("HH:mm")} - ${end.format("HH:mm")}`,
      };
    })
    .filter(Boolean);
};

const buildActiveSegments = (points, selectedDate, zoomStartTime, zoomEndTime, isActive, type) => {
  const { start: dayStart, end: dayEnd, dataEnd } = getDayBounds(selectedDate, zoomStartTime, zoomEndTime);
  const sortedPoints = points
    .map((point) => ({
      ...point,
      timelineDate: toTimelineMoment(point.created),
      io8Value: getIo8Value(point),
    }))
    .filter((point) => point.timelineDate && point.timelineDate.isValid())
    .sort((a, b) => a.timelineDate.valueOf() - b.timelineDate.valueOf());

  const segments = [];
  const pointAtStart = sortedPoints.filter((point) => !point.timelineDate.isAfter(dayStart)).pop();
  let currentStart = pointAtStart && isActive(pointAtStart) ? dayStart.clone() : null;

  sortedPoints.forEach((point, index) => {
    const pointTime = point.timelineDate;
    if (!pointTime.isAfter(dayStart)) {
      return;
    }

    const active = isActive(point);
    const nextPoint = sortedPoints[index + 1];
    const nextTime = nextPoint && nextPoint.timelineDate && nextPoint.timelineDate.isValid() ? nextPoint.timelineDate : dataEnd;

    if (!pointTime.isBefore(dataEnd) || !nextTime.isAfter(dayStart)) {
      return;
    }

    if (active && !currentStart) {
      currentStart = moment.max(pointTime, dayStart);
    }

    if (!active && currentStart) {
      segments.push({
        type,
        start: currentStart.clone(),
        end: moment.max(pointTime, dayStart),
      });
      currentStart = null;
    }

    if (active && index === sortedPoints.length - 1 && currentStart) {
      segments.push({
        type,
        start: currentStart.clone(),
        end: dataEnd.clone(),
      });
      currentStart = null;
    }
  });

  return makePositionedSegments(segments, dayStart, dayEnd, dataEnd);
};

const buildIo8StateSegments = (points, selectedDate, zoomStartTime, zoomEndTime) => {
  const { start: dayStart, end: dayEnd, dataEnd } = getDayBounds(selectedDate, zoomStartTime, zoomEndTime);
  const sortedPoints = points
    .map((point) => ({
      ...point,
      timelineDate: toTimelineMoment(point.created),
      io8Value: getIo8Value(point),
    }))
    .filter((point) => point.timelineDate && point.timelineDate.isValid() && point.io8Value !== null)
    .sort((a, b) => a.timelineDate.valueOf() - b.timelineDate.valueOf());

  if (sortedPoints.length === 0 || !dataEnd.isAfter(dayStart)) {
    return [];
  }

  const pointAtStart = sortedPoints.filter((point) => !point.timelineDate.isAfter(dayStart)).pop();
  const hasPointBeforeWindowEnd = sortedPoints.some((point) => point.timelineDate.isBefore(dataEnd));
  const hasPointInWindow = sortedPoints.some((point) => point.timelineDate.isAfter(dayStart) && point.timelineDate.isBefore(dataEnd));
  const canCarryPointAtStart = pointAtStart && pointAtStart.timelineDate.isSame(dayStart, "day");

  if (!hasPointBeforeWindowEnd || (!canCarryPointAtStart && !hasPointInWindow)) {
    return [];
  }

  const getType = (point) => (point.io8Value === 1 ? "driving" : "parked");
  const segments = [];
  let currentType = getType(canCarryPointAtStart ? pointAtStart : sortedPoints[0]);
  let currentStart = dayStart.clone();

  for (let index = 1; index < sortedPoints.length; index += 1) {
    const point = sortedPoints[index];
    const pointTime = point.timelineDate;

    if (!pointTime.isAfter(dayStart) || pointTime.isAfter(dataEnd)) {
      continue;
    }

    const pointType = getType(point);

    if (pointType !== currentType) {
      segments.push({
        type: currentType,
        start: currentStart.clone(),
        end: pointTime.clone(),
      });
      currentType = pointType;
      currentStart = pointTime.clone();
    }
  }

  segments.push({
    type: currentType,
    start: currentStart.clone(),
    end: dataEnd.clone(),
  });

  return makePositionedSegments(segments, dayStart, dayEnd, dataEnd);
};

const buildVirtualTripSegments = (virtualTrips, selectedDate, zoomStartTime, zoomEndTime) => {
  const { start: dayStart, end: dayEnd, dataEnd } = getDayBounds(selectedDate, zoomStartTime, zoomEndTime);
  const segments = (virtualTrips || [])
    .map((trip) => {
      const start = toTimelineMoment(getValue(trip, ["started", "Started"]));
      const end = toTimelineMoment(getValue(trip, ["completed", "Completed"]));

      if (!start || !end) return null;

      return {
        type: "virtual",
        start,
        end,
      };
    })
    .filter(Boolean);

  return makePositionedSegments(segments, dayStart, dayEnd, dataEnd);
};

const buildDriverSegments = (events, selectedDate, zoomStartTime, zoomEndTime) => {
  const { start: dayStart, end: dayEnd, dataEnd } = getDayBounds(selectedDate, zoomStartTime, zoomEndTime);
  const sortedEvents = (events || [])
    .map((event) => ({
      ...event,
      timelineDate: toTimelineMoment(getValue(event, ["occurredAt", "OccurredAt", "occurred_at"])),
      eventTypeValue: String(getValue(event, ["eventType", "EventType", "event_type"]) || "").toLowerCase(),
      driverLabel: String(getValue(event, ["driverName", "DriverName", "driver_name"]) || "").trim(),
    }))
    .filter((event) => event.timelineDate && event.timelineDate.isValid())
    .sort((a, b) => a.timelineDate.valueOf() - b.timelineDate.valueOf());

  if (sortedEvents.length === 0 || !dataEnd.isAfter(dayStart)) {
    return [];
  }

  const eventAtStart = sortedEvents.filter((event) => !event.timelineDate.isAfter(dayStart)).pop();
  let currentDriver = eventAtStart && eventAtStart.eventTypeValue === "pre_start" ? eventAtStart.driverLabel : "";
  let currentStart = currentDriver ? dayStart.clone() : null;
  const segments = [];

  sortedEvents.forEach((event) => {
    const eventTime = event.timelineDate;

    if (!eventTime.isAfter(dayStart) || eventTime.isAfter(dataEnd)) {
      return;
    }

    if (event.eventTypeValue === "post_trip") {
      if (currentDriver && currentStart) {
        segments.push({
          type: "driver",
          label: currentDriver,
          start: currentStart.clone(),
          end: eventTime.clone(),
        });
      }
      currentDriver = "";
      currentStart = null;
      return;
    }

    if (event.eventTypeValue !== "pre_start") {
      return;
    }

    if (currentDriver && currentStart && currentDriver !== event.driverLabel) {
      segments.push({
        type: "driver",
        label: currentDriver,
        start: currentStart.clone(),
        end: eventTime.clone(),
      });
    }

    currentDriver = event.driverLabel;
    currentStart = currentDriver ? eventTime.clone() : null;
  });

  if (currentDriver && currentStart) {
    segments.push({
      type: "driver",
      label: currentDriver,
      start: currentStart.clone(),
      end: dataEnd.clone(),
    });
  }

  return makePositionedSegments(segments, dayStart, dayEnd, dataEnd);
};

const getFutureShade = (selectedDate, zoomStartTime, zoomEndTime) => {
  const { start: dayStart, end: dayEnd, reportDataEnd } = getDayBounds(selectedDate, zoomStartTime, zoomEndTime);

  if (!reportDataEnd.isBefore(dayEnd)) {
    return null;
  }

  const dayMs = dayEnd.diff(dayStart);
  const shadeStart = moment.max(reportDataEnd, dayStart);
  const left = clampPercent((shadeStart.diff(dayStart) / dayMs) * 100);

  return {
    left,
    width: 100 - left,
    label: `Future time from ${shadeStart.format("HH:mm")}`,
  };
};

const getTimelineTicks = (selectedDate, zoomStartTime, zoomEndTime) => {
  const { start, end } = getDayBounds(selectedDate, zoomStartTime, zoomEndTime);
  const totalMs = end.diff(start);
  const totalMinutes = Math.max(15, end.diff(start, "minutes"));
  const interval = totalMinutes <= 120 ? 15 : totalMinutes <= 360 ? 30 : 60;
  const ticks = [];
  let cursor = start.clone();

  while (cursor.isBefore(end)) {
    ticks.push({
      key: cursor.format("HH:mm"),
      label: cursor.format("HH:mm"),
      left: clampPercent((cursor.diff(start) / totalMs) * 100),
      major: cursor.minutes() === 0,
    });
    cursor = cursor.add(interval, "minutes");
  }

  ticks.push({
    key: end.format("HH:mm") === "00:00" && end.diff(start, "hours") >= 1 ? "24:00" : end.format("HH:mm"),
    label: end.format("HH:mm") === "00:00" && end.diff(start, "hours") >= 1 ? "24:00" : end.format("HH:mm"),
    left: 100,
    major: true,
  });

  return ticks;
};

const getOverlayValue = (point, metricKey) => {
  const metric = OVERLAY_METRICS[metricKey];
  const value = metric ? getNumberValue(getValue(point, metric.keys)) : null;

  if (
    value === null ||
    (metric.minValidValue !== undefined && value < metric.minValidValue) ||
    (metric.maxValidValue !== undefined && value > metric.maxValidValue)
  ) {
    return null;
  }

  return value;
};

const getOverlayOptions = (points) => (
  Object.keys(OVERLAY_METRICS)
    .filter((metricKey) => (points || []).some((point) => getOverlayValue(point, metricKey) !== null))
    .map((metricKey) => ({
      value: metricKey,
      label: OVERLAY_METRICS[metricKey].label,
    }))
);

const formatOverlayScaleValue = (value, metric) => {
  if (!Number.isFinite(value)) {
    return "-";
  }

  return metric.unit === "km/h" ? `${Math.round(value)}` : `${value.toFixed(1)}`;
};

const buildOverlayChart = (points, selectedDate, zoomStartTime, zoomEndTime, metricKey) => {
  const metric = OVERLAY_METRICS[metricKey];
  if (!metric) {
    return null;
  }

  const { start: dayStart, end: dayEnd, dataEnd } = getDayBounds(selectedDate, zoomStartTime, zoomEndTime);
  const dayMs = dayEnd.diff(dayStart);
  const chartPoints = (points || [])
    .map((point) => ({
      timelineDate: toTimelineMoment(point.created),
      value: getOverlayValue(point, metricKey),
    }))
    .filter((point) =>
      point.timelineDate &&
      point.timelineDate.isValid() &&
      point.value !== null &&
      point.timelineDate.isSameOrAfter(dayStart) &&
      point.timelineDate.isSameOrBefore(dataEnd)
    )
    .sort((a, b) => a.timelineDate.valueOf() - b.timelineDate.valueOf());

  if (chartPoints.length === 0 || dayMs <= 0) {
    return null;
  }

  const values = chartPoints.map((point) => point.value);
  let minValue = Math.min(...values);
  let maxValue = Math.max(...values);

  if (metric.floorAtZero) {
    minValue = 0;
  }

  if (minValue === maxValue) {
    const padding = minValue === 0 ? 1 : Math.abs(minValue) * 0.12;
    minValue -= padding;
    maxValue += padding;
  }

  const range = maxValue - minValue;
  const pointsAttribute = chartPoints
    .map((point) => {
      const x = clampPercent((point.timelineDate.diff(dayStart) / dayMs) * 100);
      const y = clampPercent(((maxValue - point.value) / range) * 100);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  const latest = chartPoints[chartPoints.length - 1];

  return {
    metric,
    pointsAttribute,
    count: chartPoints.length,
    latestValue: latest.value,
    latestTime: latest.timelineDate.format("HH:mm"),
    minLabel: formatOverlayScaleValue(minValue, metric),
    midLabel: formatOverlayScaleValue(minValue + range / 2, metric),
    maxLabel: formatOverlayScaleValue(maxValue, metric),
  };
};

const TimelineTrack = ({ label, segments, variant, emptyLabel }) => (
  <div className="timelineTrackRow">
    <div className="timelineTrackLabel">{label}</div>
    <div className={`timelineTrackLane ${variant || ""}`}>
      {segments.length === 0 ? (
        <div className="timelineEmptyLabel">{emptyLabel || "No activity"}</div>
      ) : (
        segments.map((segment, index) => (
          <div
            key={`${label}-${index}-${segment.left}`}
            className={`timelineBlock ${segment.type}`}
            style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
            title={`${label}: ${segment.label ? `${segment.label} ` : ""}${segment.timeLabel} (${segment.durationLabel})`}
          >
            {segment.label && <span>{segment.label}</span>}
          </div>
        ))
      )}
    </div>
  </div>
);

const TimelineOverlayTrack = ({ chart, emptyLabel, ticks }) => (
  <div className="timelineTrackRow timelineOverlayRow">
    <div className="timelineTrackLabel">Chart</div>
    <div className="timelineTrackLane timelineOverlayLane">
      <div className="timelineOverlayGrid" aria-hidden="true">
        {ticks.map((tick) => (
          <span key={`chart-${tick.key}`} className={tick.major ? "major" : ""} style={{ left: `${tick.left}%` }} />
        ))}
      </div>
      {!chart ? (
        <div className="timelineEmptyLabel">{emptyLabel || "No chart data"}</div>
      ) : (
        <React.Fragment>
          <div className="timelineOverlayMeta">
            <span>{chart.metric.label}</span>
          </div>
          <svg className="timelineOverlayChart" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <polyline className={chart.metric.strokeClass} points={chart.pointsAttribute} />
          </svg>
          <div className="timelineOverlayScale" aria-hidden="true">
            <span>{chart.maxLabel}</span>
            <span>{chart.midLabel}</span>
            <span>{chart.minLabel}</span>
            <em>{chart.metric.unit}</em>
          </div>
        </React.Fragment>
      )}
    </div>
  </div>
);

class Timeline extends Component {
  constructor(props) {
    super(props);
    this.state = {
      assetPlaceholder: "Select Asset",
      dataPlaceholder: moment().format("dddd Do MMMM, YYYY"),
      groupDetails: [],
      groupId: "",
      AssetList: [],
      assetId: "",
      assetName: "",
      assetCustomerName: "",
      assetGeneralName: "",
      selectedDate: moment().format("YYYY-MM-DD"),
      reportDate: moment().format("YYYY-MM-DD"),
      calendarSelectedDate: new Date(),
      calendarShow: false,
      zoomStartTime: DEFAULT_ZOOM_START,
      zoomEndTime: DEFAULT_ZOOM_END,
      overlayEnabled: false,
      overlayMetric: "speed",
      locationData: [],
      virtualTrips: [],
      driverEvents: [],
      assetLoader: false,
      reportLoader: false,
      assetSelectionBoolean: false,
      errorLoader: false,
      errorMessage: "",
      isLoading: false,
      isDoorSensorEnabled: false,
      showDriverTimeline: true,
      checked: getSavedReportFilteringState(),
      loadedReportParams: null,
    };
    this.groupId = "";
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  async componentDidMount() {
    const values = queryString.parse(this.props.location.search);
    const searchDate = values.assetDate || values.SearchDate || values.searchDate;
    const assetId = values.assetId || values.AssetId || values.id;
    const assetName = values.assetName || values.AssetName || values.name || "Selected Asset";
    const groupId = values.groupId || values.GroupId;
    const driverParam = values.Driver || values.driver || values.showDriver;
    const urlState = {};

    if (driverParam !== undefined) {
      urlState.showDriverTimeline = getBooleanParam(driverParam, true);
    }

    if (groupId) {
      this.groupId = groupId;
      urlState.groupId = groupId;
      urlState.assetLoader = true;
      this.props.setReportGroupIdAction(groupId);
      this.getAssets(groupId);
    }

    if (assetId) {
      if (searchDate !== undefined && searchDate !== "null") {
        urlState.dataPlaceholder = moment(searchDate).format("dddd Do MMMM, YYYY");
        urlState.selectedDate = searchDate;
        urlState.calendarSelectedDate = moment(searchDate).toDate();
      }

      urlState.assetPlaceholder = assetName;
      urlState.assetName = assetName;
      urlState.assetCustomerName = assetName;
      urlState.assetId = assetId;
      this.setState(urlState, this.handleCreateReport);
    } else if (Object.keys(urlState).length > 0) {
      this.setState(urlState);
    }

    try {
      const GroupList = await axios.post(
        `${apiHost}/api/Notification/AllGroupForNotification`,
        {},
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      if (GroupList.status === 200) {
        this.setState({ isLoading: false, groupDetails: GroupList.data });
      } else {
        this.setState({ isLoading: true });
      }
    } catch (e) {
      if (e && e.response && e.response.status == "401") {
        this.props.history.push("/login");
      } else {
        this.props.setErrorMessageAction(`${e}`);
      }
    }

    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  setWrapperRef(node) {
    this.wrapperRef = node;
  }

  handleClickOutside(event) {
    if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
      this.setState({ calendarShow: false });
    }
  }

  getAssets = async (groupId) => {
    try {
      const AssetList = await axios.get(`${apiHost}/api/Notification/AssetsByGroupId/${groupId || this.groupId}?IsReport=true`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });

      this.setState({
        AssetList: AssetList.data.paggedlist,
        assetLoader: false,
      });
    } catch (e) {
      if (e && e.response && e.response.status == "401") {
        this.props.history.push("/login");
      } else {
        this.props.setErrorMessageAction(`${e}`);
      }
      this.setState({ assetLoader: false });
    }
  };

  handleGroupSelection = (selectedGroup) => {
    if (!selectedGroup) {
      this.groupId = "";
      this.setState({
        groupId: "",
        assetName: "",
        assetCustomerName: "",
        assetGeneralName: "",
        assetPlaceholder: "Select Asset",
        assetId: "",
        AssetList: [],
        assetLoader: false,
        assetSelectionBoolean: false,
        locationData: [],
        virtualTrips: [],
        driverEvents: [],
      });
      this.props.setReportGroupIdAction("");
      return;
    }

    const group = selectedGroup.data;
    this.groupId = group.groupId;
    this.setState({
      groupId: group.groupId,
      assetName: "",
      assetCustomerName: "",
      assetGeneralName: "",
      assetPlaceholder: "Select Asset",
      assetId: "",
      AssetList: [],
      assetLoader: true,
      assetSelectionBoolean: false,
      locationData: [],
      virtualTrips: [],
      driverEvents: [],
    });
    this.props.setReportGroupIdAction(group.groupId);
    this.getAssets(group.groupId);
  };

  handleAssetSelection = (selectedAsset) => {
    if (!selectedAsset) {
      this.setState({
        assetId: "",
        assetName: "",
        assetCustomerName: "",
        assetGeneralName: "",
        assetPlaceholder: "Select Asset",
        assetSelectionBoolean: false,
        locationData: [],
        virtualTrips: [],
        driverEvents: [],
      });
      return;
    }

    const asset = selectedAsset.data;
    this.setState({
      assetId: asset.assetId,
      assetName: asset.name,
      assetCustomerName: asset.name,
      assetGeneralName: asset.customerName,
      assetPlaceholder: asset.name,
    });
  };

  calendarFunction = () => {
    this.setState({
      calendarShow: !this.state.calendarShow,
    });
  };

  onChangeDate = (date) => {
    this.setState({
      calendarSelectedDate: date,
      selectedDate: moment(date).format("YYYY-MM-DD"),
      dataPlaceholder: moment(date).format("dddd Do MMMM, YYYY"),
      calendarShow: false,
    });
  };

  handleWindow = () => {
    this.props.setHeaderVisibleAction(!this.props.headerHide);
  };

  handleZoomStartChange = (event) => {
    const endMinutes = getMinutesFromTime(this.state.zoomEndTime) || DAY_MINUTES;
    const startMinutes = Math.min(Number(event.target.value), endMinutes - ZOOM_STEP_MINUTES);

    this.setState({
      zoomStartTime: formatTimeFromMinutes(startMinutes),
    });
  };

  handleZoomEndChange = (event) => {
    const startMinutes = getMinutesFromTime(this.state.zoomStartTime) || 0;
    const endMinutes = Math.max(Number(event.target.value), startMinutes + ZOOM_STEP_MINUTES);

    this.setState({
      zoomEndTime: formatTimeFromMinutes(endMinutes),
    });
  };

  resetZoom = () => {
    this.setState({
      zoomStartTime: DEFAULT_ZOOM_START,
      zoomEndTime: DEFAULT_ZOOM_END,
    });
  };

  isSystemAdmin = () => localStorage.getItem("role") === "SystemAdmin";

  handleOverlayToggle = (event) => {
    this.setState({
      overlayEnabled: event.target.checked,
    });
  };

  handleOverlayMetricChange = (event) => {
    this.setState({
      overlayMetric: event.target.value,
    });
  };

  getQuickReportUrl = (path) => {
    const assetName = this.state.assetName || this.state.assetCustomerName || this.state.assetPlaceholder || "";
    const params = new URLSearchParams({
      assetId: this.state.assetId,
      assetName,
      SearchDate: this.state.selectedDate,
      assetDate: this.state.selectedDate,
      UseFiltering: String(this.state.checked),
      Driver: String(this.state.showDriverTimeline),
    });

    if (this.state.groupId) {
      params.set("groupId", this.state.groupId);
    }

    return `${path}?${params.toString()}`;
  };

  openQuickReport = (path) => {
    if (!this.state.assetId) {
      return;
    }

    const href = this.getQuickReportUrl(path);
    const parentWindow = window.opener && !window.opener.closed ? window.opener : window;
    const reportWindow = parentWindow.open(href, "_blank");

    if (reportWindow) {
      reportWindow.focus();
      return;
    }

    window.open(href, "_blank");
  };

  updateCurrentReportUrl = () => {
    const href = this.getQuickReportUrl(this.props.location.pathname);
    this.props.history.replace(href);
  };

  handleCreateReport = async () => {
    if (!this.state.assetId) {
      this.setState({
        assetSelectionBoolean: false,
        errorLoader: true,
        reportLoader: false,
        errorMessage: "Select an asset before creating the Timeline report.",
      });
      return;
    }

    const reportDate = this.state.selectedDate || moment().format("YYYY-MM-DD");

    this.setState({
      locationData: [],
      virtualTrips: [],
      driverEvents: [],
      assetSelectionBoolean: true,
      reportLoader: true,
      errorLoader: false,
      errorMessage: "",
    });

    this.props.setReportAssetIdAction(this.state.assetId);
    this.props.setReportAssetNameAction(this.state.assetName || this.state.assetCustomerName);
    this.props.setReportCalendatDateAction(reportDate);
    this.props.setReportGroupIdAction(this.state.groupId);
    this.updateCurrentReportUrl();
    this.setState({
      loadedReportParams: {
        assetId: this.state.assetId,
        selectedDate: reportDate,
        groupId: this.state.groupId,
        checked: this.state.checked,
        showDriverTimeline: this.state.showDriverTimeline,
      },
    });

    try {
      const request = {
        AssetId: this.state.assetId,
        SearchDate: reportDate,
        Timezone: localStorage.getItem("timeZoneID"),
        UseFiltering: this.state.checked,
      };
      const driverDayStartUtc = moment.tz(reportDate, "YYYY-MM-DD", getTimeZone()).startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
      const driverDayEndUtc = moment.tz(reportDate, "YYYY-MM-DD", getTimeZone()).startOf("day").add(1, "day").utc().format("YYYY-MM-DD HH:mm:ss");

      const driverEventsRequest = this.state.showDriverTimeline
        ? axios.get(`${apiHost}/api/Assets/${this.state.assetId}/DriverEvents`, {
            params: {
              date: reportDate,
              startUtc: driverDayStartUtc,
              endUtc: driverDayEndUtc,
            },
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          })
        : Promise.resolve({ data: { rows: [] } });

      const [locationData, tripLogData, driverEventsData] = await Promise.all([
        axios.post(`${apiHost}/api/Report/LocationDataReport`, request, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }),
        axios.post(`${apiHost}/api/Report/MVTripLog`, request, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }),
        driverEventsRequest,
      ]);

      this.setState({
        reportDate,
        locationData: locationData.data.locationData || [],
        virtualTrips: tripLogData.data.virtualTrips || [],
        driverEvents: driverEventsData.data.rows || [],
        isDoorSensorEnabled: locationData.data.doorSensor || tripLogData.data.doorSensor,
        reportLoader: false,
        errorLoader: false,
      });
    } catch (e) {
      if (e && e.response && e.response.status == "401") {
        this.props.history.push("/login");
      } else {
        this.setState({
          errorLoader: true,
          errorMessage: `${e}`,
        });
        this.props.setErrorMessageAction(`${e}`);
      }
      this.setState({ reportLoader: false });
    }
  };

  getTimelineData = () => {
    const points = this.state.locationData || [];
    const selectedDate = this.state.reportDate || this.state.selectedDate;
    const zoomStartTime = this.state.zoomStartTime;
    const zoomEndTime = this.state.zoomEndTime;

    const driveSegments = buildIo8StateSegments(points, selectedDate, zoomStartTime, zoomEndTime);

    const fridgeSegments = buildActiveSegments(
      points,
      selectedDate,
      zoomStartTime,
      zoomEndTime,
      (point) => isFridgeOn(getValue(point, ["fridge", "Fridge", "iO1", "IO1", "io1"])),
      "fridge"
    );

    const doorSegments = buildActiveSegments(
      points,
      selectedDate,
      zoomStartTime,
      zoomEndTime,
      (point) => isDoorOpen(getValue(point, ["doorSensorStatus", "DoorSensorStatus", "iO2", "IO2", "io2"])),
      "door"
    );

    const virtualTripSegments = buildVirtualTripSegments(this.state.virtualTrips, selectedDate, zoomStartTime, zoomEndTime);
    const driverSegments = buildDriverSegments(this.state.driverEvents, selectedDate, zoomStartTime, zoomEndTime);

    return {
      driveSegments,
      fridgeSegments,
      doorSegments,
      virtualTripSegments,
      driverSegments,
    };
  };

  renderTimeline = () => {
    const { driveSegments, fridgeSegments, doorSegments, virtualTripSegments, driverSegments } = this.getTimelineData();
    const reportDate = this.state.reportDate || this.state.selectedDate;
    const reportDateLabel = moment(reportDate).format("dddd Do MMMM, YYYY");
    const ticks = getTimelineTicks(reportDate, this.state.zoomStartTime, this.state.zoomEndTime);
    const overlayOptions = getOverlayOptions(this.state.locationData);
    const selectedOverlayMetric = overlayOptions.some((option) => option.value === this.state.overlayMetric)
      ? this.state.overlayMetric
      : (overlayOptions[0] && overlayOptions[0].value) || "speed";
    const showOverlay = this.isSystemAdmin() && this.state.overlayEnabled;
    const overlayChart = showOverlay
      ? buildOverlayChart(this.state.locationData, reportDate, this.state.zoomStartTime, this.state.zoomEndTime, selectedOverlayMetric)
      : null;
    const totalRows = (this.state.isDoorSensorEnabled ? 4 : 3) + (showOverlay ? 1 : 0) + (this.state.showDriverTimeline ? 1 : 0);
    const futureShade = getFutureShade(reportDate, this.state.zoomStartTime, this.state.zoomEndTime);
    const zoomStartMinutes = getMinutesFromTime(this.state.zoomStartTime) || 0;
    const zoomEndMinutes = getMinutesFromTime(this.state.zoomEndTime) || DAY_MINUTES;
    const zoomLeft = (zoomStartMinutes / DAY_MINUTES) * 100;
    const zoomWidth = ((zoomEndMinutes - zoomStartMinutes) / DAY_MINUTES) * 100;

    return (
      <div className="timelineReportPanel">
        <div className="timelineTopBar">
          <div className="timelineLegend">
            <span><i className="timelineLegendSwatch parked" />Parked</span>
            <span><i className="timelineLegendSwatch driving" />Driving</span>
            <span><i className="timelineLegendSwatch virtual" />Virtual trip</span>
            <span><i className="timelineLegendSwatch fridge" />Fridge on</span>
            {this.state.isDoorSensorEnabled && <span><i className="timelineLegendSwatch door" />Door open</span>}
            {this.state.showDriverTimeline && <span><i className="timelineLegendSwatch driver" />Driver ID</span>}
          </div>
          <div className="timelineReportDate">
            <span>Report date</span>
            <strong>{reportDateLabel}</strong>
          </div>
          {this.isSystemAdmin() && (
            <div className="timelineOverlayControls">
              <label className="timelineOverlayToggle">
                <input type="checkbox" checked={this.state.overlayEnabled} onChange={this.handleOverlayToggle} />
                <span>Admin feature</span>
              </label>
              <select
                value={selectedOverlayMetric}
                onChange={this.handleOverlayMetricChange}
                disabled={!this.state.overlayEnabled || overlayOptions.length === 0}
              >
                {overlayOptions.length === 0 ? (
                  <option value="speed">No data</option>
                ) : (
                  overlayOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))
                )}
              </select>
            </div>
          )}
        </div>
        <div className="timelineCanvas" style={{ "--timeline-row-count": totalRows, "--timeline-overlay-extra-height": showOverlay ? "108px" : "0px" }}>
          <div className="timelineGrid" aria-hidden="true">
            {ticks.map((tick) => (
              <span key={tick.key} className={tick.major ? "major" : ""} style={{ left: `${tick.left}%` }} />
            ))}
          </div>
          {futureShade && (
            <div className="timelineFutureShadeLayer" title={futureShade.label}>
              <div
                className="timelineFutureShade"
                style={{ left: `${futureShade.left}%`, width: `${futureShade.width}%` }}
              />
            </div>
          )}
          {showOverlay && <TimelineOverlayTrack chart={overlayChart} ticks={ticks} emptyLabel="No numeric chart data" />}
          <TimelineTrack label="Virtual Trips" segments={virtualTripSegments} variant="thin" emptyLabel="No virtual trips" />
          <TimelineTrack label="Parked / Driving" segments={driveSegments} emptyLabel="No IO8 data" />
          <TimelineTrack label="Fridge On" segments={fridgeSegments} variant="thin" emptyLabel="Fridge off" />
          {this.state.isDoorSensorEnabled && <TimelineTrack label="Door Open" segments={doorSegments} variant="thin" emptyLabel="Door closed" />}
          {this.state.showDriverTimeline && <TimelineTrack label="Driver ID" segments={driverSegments} variant="driver" emptyLabel="No driver login" />}
          <div className="timelineTimecode">
            {ticks.map((tick) => (
              <span key={tick.key} className={tick.major ? "major" : ""} style={{ left: `${tick.left}%` }}>
                {tick.label}
              </span>
            ))}
          </div>
        </div>
        <div className="timelineZoomControls">
          <div className="timelineZoomHeader">
            <span className="timelineZoomLabel">Zoom</span>
            <strong>{this.state.zoomStartTime} - {this.state.zoomEndTime}</strong>
            <button type="button" onClick={this.resetZoom}>Reset</button>
          </div>
          <div className="timelineRangeSlider">
            <div className="timelineRangeTrack" />
            <div className="timelineRangeFill" style={{ left: `${zoomLeft}%`, width: `${zoomWidth}%` }} />
            <input
              aria-label="Timeline zoom start"
              type="range"
              min="0"
              max={DAY_MINUTES - ZOOM_STEP_MINUTES}
              step={ZOOM_STEP_MINUTES}
              value={zoomStartMinutes}
              onChange={this.handleZoomStartChange}
            />
            <input
              aria-label="Timeline zoom end"
              type="range"
              min={ZOOM_STEP_MINUTES}
              max={DAY_MINUTES}
              step={ZOOM_STEP_MINUTES}
              value={zoomEndMinutes}
              onChange={this.handleZoomEndChange}
            />
          </div>
          <div className="timelineRangeLabels">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>24:00</span>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const selectStyles = {
      control: (base, state) => ({
        ...base,
        minHeight: 44,
        backgroundColor: "#f8fafc",
        borderColor: state.isFocused ? "#244b8f" : "#dbe4ef",
        borderRadius: 8,
        boxShadow: state.isFocused ? "0 0 0 2px rgba(36, 75, 143, 0.14)" : "none",
        "&:hover": {
          borderColor: state.isFocused ? "#244b8f" : "#aebdd0",
        },
      }),
      indicatorSeparator: (base) => ({
        ...base,
        backgroundColor: "#dbe4ef",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? "#244b8f" : state.isFocused ? "#edf3fb" : "#fff",
        color: state.isSelected ? "#fff" : "#26354f",
        fontSize: 13,
      }),
      placeholder: (base) => ({
        ...base,
        color: "#7e8da1",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#17233c",
        fontWeight: 700,
      }),
      menu: (base) => ({
        ...base,
        zIndex: 200,
        borderRadius: 8,
        overflow: "hidden",
      }),
    };

    const groupOptions = this.state.groupDetails.map((group) => ({
      value: group.groupId,
      label: `${group.customername} - ${group.groupName}`,
      data: group,
    }));

    const selectedGroupOption = groupOptions.find((option) => option.value == this.state.groupId) || null;

    const assetOptions = this.state.AssetList.map((asset) => ({
      value: asset.assetId,
      label: `${asset.name}${asset.customerName ? ` - ${asset.customerName}` : ""}`,
      data: asset,
    }));

    const selectedAssetOption =
      assetOptions.find((option) => option.value == this.state.assetId) ||
      (this.state.assetId
        ? {
            value: this.state.assetId,
            label: this.state.assetPlaceholder,
            data: {
              assetId: this.state.assetId,
              name: this.state.assetPlaceholder,
              customerName: this.state.assetGeneralName,
            },
          }
        : null);
    const loadedReportParams = this.state.loadedReportParams;
    const hasPendingReportChanges = !!(
      loadedReportParams &&
      this.state.assetId &&
      (
        loadedReportParams.assetId !== this.state.assetId ||
        loadedReportParams.selectedDate !== this.state.selectedDate ||
        (loadedReportParams.groupId || "") !== (this.state.groupId || "") ||
        loadedReportParams.checked !== this.state.checked ||
        loadedReportParams.showDriverTimeline !== this.state.showDriverTimeline
      )
    );

    const calendarReport = this.state.calendarShow ? (
      <div ref={this.setWrapperRef} className="ParentCalender" style={{ position: "relative", zIndex: 100 }}>
        <div className="CalendarContainer" style={{ position: "absolute" }}>
          <Calendar maxDate={new Date()} onChange={this.onChangeDate} value={this.state.calendarSelectedDate} />
        </div>
      </div>
    ) : (
      ""
    );

    return (
      <Layout>
        <Helmet>
          <title>Timeline</title>
        </Helmet>
        <div className="contentBody">
          <div className={!this.props.headerHide ? `contentWrapper contentWrapperSmoothRelease report-modern-page timeline-report-page` : `contentWrapper contentWrapperSmooth report-modern-page timeline-report-page`}>
            <div className="reportHeaderTile">
              <div className="headingContainer report-modern-header">
                <div>
                  <h2 className="mainHeading">Timeline Report</h2>
                  <p>Review a selected asset day as stacked activity, virtual trip and sensor tracks.</p>
                </div>
                <i className={this.props.headerHide ? `fa fa-window-restore` : `fa fa-window-maximize`} onClick={this.handleWindow} />
              </div>
              <div className="inputFormGroup reportPickerGrid reportPickerToolbar">
                <div className="reportQuickActions">
                  <span className="reportQuickActionsLabel">Quick reports</span>
                  <button type="button" disabled={!this.state.assetId} onClick={() => this.openQuickReport("/Rpt-MVTL")}>MVTL</button>
                  <button type="button" disabled={!this.state.assetId} onClick={() => this.openQuickReport("/Rpt-Visit")}>Visit</button>
                  <button type="button" disabled={!this.state.assetId} onClick={() => this.openQuickReport("/Temperature")}>Temp</button>
                  <button type="button" disabled={!this.state.assetId} onClick={() => this.openQuickReport("/Replay")}>Replay</button>
                  <button type="button" disabled={!this.state.assetId} onClick={() => this.openQuickReport("/Rpt-LocationData")}>Location</button>
                  <button type="button" className="active" disabled={!this.state.assetId} onClick={() => this.openQuickReport("/Timeline")}>Timeline</button>
                  <button type="button" disabled={!this.state.assetId} onClick={() => this.openQuickReport("/Track")}>Track</button>
                </div>
                <div className="inputElement reportPickerField">
                  <label>Group</label>
                  <Select
                    value={selectedGroupOption}
                    options={groupOptions}
                    styles={selectStyles}
                    onChange={this.handleGroupSelection}
                    isClearable
                    isSearchable
                    placeholder="Search group"
                    noOptionsMessage={() => "No groups found"}
                  />
                  {this.state.isLoading && (
                    <GroupWarning
                      warningText={true}
                      warningTextHead="No Groups Found"
                      warningTextBody=" You do not have any group memberships. Please contact Support to ensure your account configured correctly."
                    />
                  )}
                </div>
                <div className="inputElement reportPickerField">
                  <label>Asset</label>
                  <Select
                    value={selectedAssetOption}
                    options={assetOptions}
                    styles={selectStyles}
                    onChange={this.handleAssetSelection}
                    isClearable
                    isSearchable
                    isDisabled={!this.state.groupId && !this.state.assetId}
                    isLoading={this.state.assetLoader && this.state.groupId != ""}
                    placeholder={this.state.groupId ? "Search asset" : "Select group first"}
                    noOptionsMessage={() => "No assets found"}
                  />
                </div>
                <div className="inputElement reportPickerField">
                  <label>Date</label>
                  <ReportButtonGroup
                    function={this.calendarFunction}
                    dropDown={calendarReport}
                    placeholder={this.state.dataPlaceholder}
                    icon="fa-calendar"
                  />
                </div>
                <div className="inputElement reportPickerActions">
                  <Button className={hasPendingReportChanges ? "reportPickerCreateButton report-create-pending" : "reportPickerCreateButton"} variant="primary" onClick={this.handleCreateReport} size="sm" disabled={!this.state.assetId}>
                    <i className="fa fa-tasks"></i>
                    Create Report
                  </Button>
                </div>
              </div>
            </div>
            <TimeZoneConvert />
            <div className="reportBody">
              {this.state.reportLoader ? (
                <div className="loaderLine">
                  <br /> <GridLoader size={7} color={"#222222"} loading={this.state.reportLoader} />
                  <h1> &nbsp;Generating Report</h1>
                  <br />
                </div>
              ) : this.state.errorLoader ? (
                <AssetInforWarning warningText={true} warningTextMsg={this.state.errorMessage || "No Data Available"} />
              ) : this.state.assetSelectionBoolean ? (
                this.state.locationData.length > 0 ? this.renderTimeline() : <AssetInforWarning warningText={true} warningTextMsg="No timeline data available for this asset and date." />
              ) : (
                <AssetInforWarning />
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }
}

const mapStateToProps = (state) => ({
  headerHide: state.headerHide,
});

const mapDispatchToProps = {
  setErrorMessageAction: setErrorMessage,
  setHeaderVisibleAction: setHeaderVisible,
  setReportAssetNameAction: setReportAssetName,
  setReportAssetIdAction: setReportAssetId,
  setReportCalendatDateAction: setReportCalendatDate,
  setReportGroupIdAction: setReportGroupId,
};

export default connect(mapStateToProps, mapDispatchToProps)(Timeline);
